import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    CardActions,
    Grid,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Snackbar,
    Typography,
    Box,
    Chip,
    LinearProgress,
    IconButton
} from '@mui/material';
import { 
    Upgrade as UpgradeIcon, 
    Refresh as RefreshIcon, 
    CreditCard as CreditCardIcon, 
    History as HistoryIcon, 
    TrendingUp as TrendingUpIcon 
} from '@mui/icons-material';
import merchantAPI from '../../../services/merchantAPI';
import FlutterServicesAPI from '../../../services/flutterServices';
import './MerchantSubscription.css';

const MerchantSubscription = () => {
    const tokenLabel = FlutterServicesAPI.getCreditLabel();
    const tokenLower = tokenLabel.toLowerCase();
    const [dashboardData, setDashboardData] = useState({});
    const [packages, setPackages] = useState([]);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [subscriptionHistory, setSubscriptionHistory] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showTransactionsModal, setShowTransactionsModal] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });

    useEffect(() => {
        loadDashboardData();
        loadPackages();
        loadNotifications();
    }, []);

    const loadDashboardData = async () => {
        try {
            const response = await merchantAPI.get('/subscription/dashboard');
            setDashboardData(response.data);
            
            // Also load current subscription details
            const subResponse = await merchantAPI.get('/subscription/current');
            setCurrentSubscription(subResponse.data.subscription || null);
            
        } catch (error) {
            console.error('Error loading dashboard:', error);
            showAlert('Error loading dashboard data', 'danger');
        }
    };

    const loadPackages = async () => {
        try {
            const response = await merchantAPI.get('/subscription/packages');
            setPackages(response.data.packages);
        } catch (error) {
            console.error('Error loading packages:', error);
            showAlert('Error loading packages', 'danger');
        }
    };

    const loadNotifications = async () => {
        try {
            const response = await merchantAPI.get('/subscription/notifications');
            setNotifications(response.data.notifications);
        } catch (error) {
            console.error('Error loading notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadSubscriptionHistory = async () => {
        try {
            const response = await merchantAPI.get('/subscription/history');
            setSubscriptionHistory(response.data.subscriptions);
        } catch (error) {
            console.error('Error loading subscription history:', error);
            showAlert('Error loading subscription history', 'danger');
        }
    };

    const loadTransactions = async () => {
        try {
            const response = await merchantAPI.get('/subscription/transactions');
            setTransactions(response.data.transactions);
        } catch (error) {
            console.error('Error loading transactions:', error);
            showAlert('Error loading transactions', 'danger');
        }
    };

    const showAlert = (message, type = 'success') => {
        // Map bootstrap alert types to Material-UI severity
        const severityMap = {
            'success': 'success',
            'danger': 'error',
            'warning': 'warning',
            'info': 'info'
        };
        setAlert({ show: true, message, type: severityMap[type] || 'success' });
    };

    const handlePackageSelect = async (pkg) => {
        try {
            const response = await merchantAPI.get(`/subscription/packages/${pkg.id}/preview`);
            setSelectedPackage({ ...pkg, preview: response.data });
            setShowPurchaseModal(true);
        } catch (error) {
            console.error('Error previewing package:', error);
            showAlert('Error loading package preview', 'danger');
        }
    };

    const handlePurchase = async () => {
        try {
            const response = await merchantAPI.post(`/subscription/purchase/${selectedPackage.id}`, {
                payment_reference: `DEMO_${Date.now()}`
            });
            
            showAlert('Package purchased successfully!', 'success');
            setShowPurchaseModal(false);
            loadDashboardData();
            loadPackages();
            
        } catch (error) {
            console.error('Error purchasing package:', error);
            showAlert(error.response?.data?.error || 'Error purchasing package', 'danger');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount);
    };

    const formatCredits = (credits) => {
        return new Intl.NumberFormat('en-IN').format(credits);
    };

    const getPackageTypeBadge = (type) => {
        const typeMap = {
            'FREE': 'success',
            'PAID': 'primary',
            'CUSTOM': 'warning',
            'TOPUP': 'info'
        };
        return <Chip label={type} color={typeMap[type] || 'default'} />;
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'ACTIVE': 'success',
            'EXPIRED': 'danger',
            'CANCELLED': 'secondary',
            'SUSPENDED': 'warning'
        };
        return <Chip label={status} color={statusMap[status] || 'default'} />;
    };

    const getRelationshipBadge = (relationship) => {
        switch(relationship) {
            case 'current':
                return <Chip label="Current Plan" color="success" />;
            case 'upgrade':
                return <Chip label="Upgrade" color="warning" />;
            case 'downgrade':
                return <Chip label="Downgrade" color="info" />;
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <div className="merchant-subscription">
                                     <Box sx={{ textAlign: 'center', py: 5 }}>
                         <Typography variant="body1">
                             Loading...
                         </Typography>
                     </Box>
            </div>
        );
    }

    return (
        <div className="merchant-subscription">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" component="h2">
                    My Subscription
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            loadSubscriptionHistory();
                            setShowHistoryModal(true);
                        }}
                        startIcon={<HistoryIcon />}
                    >
                        History
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            loadTransactions();
                            setShowTransactionsModal(true);
                        }}
                        startIcon={<TrendingUpIcon />}
                    >
                        Transactions
                    </Button>
                </Box>
            </Box>

                          <Snackbar
                  open={alert.show}
                  autoHideDuration={5000}
                  onClose={() => setAlert({ show: false, message: '', type: '' })}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                  <Alert 
                      onClose={() => setAlert({ show: false, message: '', type: '' })} 
                      severity={alert.type}
                      sx={{ width: '100%' }}
                  >
                      {alert.message}
                  </Alert>
              </Snackbar>

            {/* Notifications */}
            {notifications.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    {notifications.map((notification, index) => (
                        <Alert key={index} severity={notification.type === 'error' ? 'error' : notification.type} sx={{ mb: 2 }}>
                            <Typography variant="body2" component="div">
                                <strong>{notification.title}</strong>
                                <div>{notification.message}</div>
                            </Typography>
                        </Alert>
                    ))}
                </Box>
            )}

            {/* Current Subscription */}
            {currentSubscription ? (
                <Card sx={{ mb: 4 }}>
                    <CardContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6">Package Details</Typography>
                                <p><strong>Plan:</strong> {currentSubscription.package_name}</p>
                                <p><strong>Type:</strong> {getPackageTypeBadge(currentSubscription.package?.package_type)}</p>
                                <p><strong>Status:</strong> {getStatusBadge(currentSubscription.status)}</p>
                                <p><strong>Started:</strong> {new Date(currentSubscription.subscription_start_date).toLocaleDateString()}</p>
                                <p><strong>Expires:</strong> {new Date(currentSubscription.package_expiry_date).toLocaleDateString()}</p>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6">{tokenLabel} usage</Typography>
                                <Box sx={{ mb: 3 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>{tokenLabel} used</span>
                                        <span>{formatCredits(currentSubscription.credits_used)} / {formatCredits(currentSubscription.initial_credits)}</span>
                                    </Box>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={currentSubscription.usage_percentage} 
                                        color={currentSubscription.usage_percentage > 80 ? 'error' : currentSubscription.usage_percentage > 60 ? 'warning' : 'success'}
                                    />
                                    <Typography variant="body2" color="textSecondary">{currentSubscription.usage_percentage.toFixed(1)}% used</Typography>
                                </Box>
                                <p><strong>Remaining {tokenLabel}:</strong> {formatCredits(currentSubscription.remaining_credits)}</p>
                                <p><strong>Days Until Expiry:</strong> {currentSubscription.days_until_package_expiry}</p>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            ) : (
                <Card sx={{ mb: 4 }}>
                                    <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h5">No Active Subscription</Typography>
                    <Typography variant="body2" color="textSecondary">{`Choose a subscription plan to get started with ${tokenLower}.`}</Typography>
                </CardContent>
                </Card>
            )}

            {/* Available Packages */}
            <Card>
                <CardContent>
                                         <Typography variant="h5" sx={{ mb: 0 }}>Available Plans</Typography>
                    <Grid container spacing={2}>
                        {packages.map(pkg => (
                                                         <Grid item xs={12} sm={6} md={4} key={pkg.id}>
                                  <Card className={`package-card ${pkg.relationship === 'current' ? 'current' : pkg.relationship === 'upgrade' ? 'upgrade' : pkg.relationship === 'downgrade' ? 'downgrade' : ''}`}>
                                                                         <CardContent sx={{ textAlign: 'center' }}>
                                         <Typography variant="h6" sx={{ mb: 0 }}>
                                            {pkg.name}
                                            {getRelationshipBadge(pkg.relationship)}
                                        </Typography>
                                                                                 <Box sx={{ mt: 2 }}>
                                            {getPackageTypeBadge(pkg.package_type)}
                                        </Box>
                                    </CardContent>
                                                                         <CardContent sx={{ textAlign: 'center' }}>
                                                                                  <Box className="price-display" sx={{ mb: 3 }}>
                                             <Typography variant="h3" color="primary">
                                                 {pkg.price === 0 ? 'FREE' : formatCurrency(pkg.price)}
                                             </Typography>
                                         </Box>
                                         <Box className="features" sx={{ mb: 3 }}>
                                            <p><strong>{formatCredits(pkg.credits)}</strong> {tokenLabel}</p>
                                            <p><strong>{pkg.validity_days}</strong> Days Validity</p>
                                            {pkg.carry_forward_credits > 0 && (
                                                                                <Typography variant="body2" color="success">
                                    + {formatCredits(pkg.carry_forward_credits)} carry forward
                                </Typography>
                                            )}
                                        </Box>
                                        <Typography variant="body2" color="textSecondary">{pkg.description}</Typography>
                                    </CardContent>
                                    <CardActions sx={{ justifyContent: 'center' }}>
                                        {pkg.relationship === 'current' ? (
                                            <Button variant="outlined" disabled>
                                                Current Plan
                                            </Button>
                                        ) : (
                                            <Button
                                                variant={pkg.relationship === 'upgrade' ? 'warning' : 'contained'}
                                                onClick={() => handlePackageSelect(pkg)}
                                            >
                                                {pkg.relationship === 'upgrade' && <UpgradeIcon />}
                                                {pkg.relationship === 'downgrade' && <RefreshIcon />}
                                                {pkg.relationship === 'new' && <CreditCardIcon />}
                                                {pkg.relationship === 'upgrade' ? 'Upgrade' : 
                                                 pkg.relationship === 'downgrade' ? 'Downgrade' : 
                                                 pkg.relationship === 'equivalent' ? 'Renew' : 'Purchase'}
                                            </Button>
                                        )}
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>

            {/* Purchase Modal */}
            <Dialog open={showPurchaseModal} onClose={() => setShowPurchaseModal(false)} maxWidth="lg">
                <DialogTitle>
                    {selectedPackage?.preview?.purchase_type === 'upgrade' ? 'Upgrade Package' :
                     selectedPackage?.preview?.purchase_type === 'downgrade' ? 'Downgrade Package' :
                     selectedPackage?.preview?.purchase_type === 'renewal' ? 'Renew Package' : 'Purchase Package'}
                </DialogTitle>
                <DialogContent>
                    {selectedPackage && (
                        <Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6">Package Details</Typography>
                                    <p><strong>Name:</strong> {selectedPackage.name}</p>
                                    <p><strong>Price:</strong> {selectedPackage.price === 0 ? 'FREE' : formatCurrency(selectedPackage.price)}</p>
                                    <p><strong>{tokenLabel}:</strong> {formatCredits(selectedPackage.credits)}</p>
                                    <p><strong>Validity:</strong> {selectedPackage.validity_days} days</p>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6">Purchase Summary</Typography>
                                    <p><strong>Purchase Type:</strong> {selectedPackage.preview?.purchase_type?.toUpperCase()}</p>
                                    <p><strong>New {tokenLabel}:</strong> {formatCredits(selectedPackage.preview?.credits_after_purchase || 0)}</p>
                                    {selectedPackage.preview?.carry_forward_credits > 0 && (
                                        <p><strong>Carry Forward:</strong> {formatCredits(selectedPackage.preview.carry_forward_credits)}</p>
                                    )}
                                    <p><strong>Total {tokenLabel}:</strong> {formatCredits(selectedPackage.preview?.total_credits || 0)}</p>
                                    <p><strong>Valid Until:</strong> {selectedPackage.preview?.validity_until ? new Date(selectedPackage.preview.validity_until).toLocaleDateString() : 'N/A'}</p>
                                </Grid>
                            </Grid>
                            
                            {selectedPackage.preview?.warnings?.length > 0 && (
                                <Alert severity="warning">
                                    <Typography variant="body2" component="div">
                                        <strong>Please Note:</strong>
                                        <ul style={{ margin: '8px 0 0 0' }}>
                                            {selectedPackage.preview.warnings.map((warning, index) => (
                                                <li key={index}>{warning}</li>
                                            ))}
                                        </ul>
                                    </Typography>
                                </Alert>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setShowPurchaseModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handlePurchase}>
                        {selectedPackage?.price === 0 ? 'Activate Free Plan' : `Pay ${selectedPackage ? formatCurrency(selectedPackage.price) : ''}`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* History Modal */}
            <Dialog open={showHistoryModal} onClose={() => setShowHistoryModal(false)} maxWidth="lg">
                <DialogTitle>Subscription History</DialogTitle>
                <DialogContent>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Package</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Start Date</TableCell>
                                    <TableCell>End Date</TableCell>
                                    <TableCell>{tokenLabel}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {subscriptionHistory.map(sub => (
                                    <TableRow key={sub.id}>
                                        <TableCell>{sub.package_name}</TableCell>
                                        <TableCell>{getPackageTypeBadge(sub.package_type)}</TableCell>
                                        <TableCell>{getStatusBadge(sub.status)}</TableCell>
                                        <TableCell>{new Date(sub.subscription_start_date).toLocaleDateString()}</TableCell>
                                        <TableCell>{new Date(sub.package_expiry_date).toLocaleDateString()}</TableCell>
                                        <TableCell>{formatCredits(sub.initial_credits)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
            </Dialog>

            {/* Transactions Modal */}
            <Dialog open={showTransactionsModal} onClose={() => setShowTransactionsModal(false)} maxWidth="lg">
                <DialogTitle>{tokenLabel} transactions</DialogTitle>
                <DialogContent>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>{tokenLabel}</TableCell>
                                    <TableCell>Balance</TableCell>
                                    <TableCell>Description</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactions.map(txn => (
                                    <TableRow key={txn.id}>
                                        <TableCell>{new Date(txn.created_at).toLocaleDateString()}</TableCell>
                                        <TableCell>
                                            <Chip label={txn.transaction_type} color={txn.transaction_type === 'DEBIT' ? 'error' : 'success'} />
                                        </TableCell>
                                        <TableCell className={txn.transaction_type === 'DEBIT' ? 'text-danger' : 'text-success'}>
                                            {txn.transaction_type === 'DEBIT' ? '-' : '+'}{formatCredits(txn.credits)}
                                        </TableCell>
                                        <TableCell>{formatCredits(txn.balance_after)}</TableCell>
                                        <TableCell>{txn.description}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MerchantSubscription; 